import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft, ArrowRight, ArrowUpRight, ArrowDownRight, Globe2, ExternalLink,
  Info, AlertCircle, Building2, Layers, BarChart3, LineChart as LineChartIcon, Users, Calculator, Bot,
  TrendingUp, TrendingDown, RefreshCcw, Sparkles, Zap, Loader2, MoreVertical,
} from "lucide-react";
import {
  AreaChart, Area, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { apiGet, apiPost } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import UsDeepAnalysisSection from "@/components/stocks/UsDeepAnalysisSection";
import { DataSourceNote, AiAnalysisWarning } from "@/components/legal/DataNotes";

// ---- Formatters ----
const fmtUsd = (v, opts = {}) => {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  if (opts.compact && Math.abs(n) >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (opts.compact && Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (opts.compact && Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(0)}M`;
  return `$${n.toFixed(2)}`;
};
const fmtNum = (v, d = 2) => (v == null || isNaN(v) ? "—" : Number(v).toFixed(d));
const fmtPct = (v, d = 1) => (v == null || isNaN(v) ? "—" : `${Number(v).toFixed(d)}%`);
const fmtFracPct = (v, d = 1) => (v == null || isNaN(v) ? "—" : `${(Number(v) * 100).toFixed(d)}%`);
const fmtDate = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return v; }
};

// ---- Display helpers ----
const SIGNAL_CLASS = {
  STRONG_BUY: "bg-emerald-600 text-white",
  BUY: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30",
  WATCH: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30",
  HOLD: "bg-muted text-muted-foreground",
  AVOID: "bg-destructive/15 text-destructive border border-destructive/30",
};

const SignalBadge = ({ signal, large = false }) => {
  if (!signal) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <Badge className={`${SIGNAL_CLASS[signal] || ""} ${large ? "text-base px-3 py-1" : "text-xs"} font-medium`}>
      {signal.replace("_", " ")}
    </Badge>
  );
};

const VAL_META = {
  "Deep Value":   { cls: "bg-[#14532d] text-white border-[#14532d]",  icon: "💎" },
  "Undervalued":  { cls: "bg-[#16a34a] text-white border-[#16a34a]",  icon: "📉" },
  "Fair Value":   { cls: "bg-[#6b7280] text-white border-[#6b7280]",  icon: "⚖" },
  "Overvalued":   { cls: "bg-[#ea580c] text-white border-[#ea580c]",  icon: "📈" },
  "Overpriced":   { cls: "bg-[#dc2626] text-white border-[#dc2626]",  icon: "🔴" },
};
const ValuationBadge = ({ status }) => {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>;
  const meta = VAL_META[status];
  if (!meta) return <Badge variant="outline" className="text-xs">{status}</Badge>;
  return (
    <Badge
      variant="outline"
      data-testid={`us-detail-valuation-${status.toLowerCase().replace(/\s+/g, "-")}`}
      className={`${meta.cls} text-xs font-medium inline-flex items-center gap-1 px-2.5 py-0.5`}
    >
      <span aria-hidden="true" className="leading-none">{meta.icon}</span>
      {status}
    </Badge>
  );
};

const TYPE_LABELS = {
  mega_cap_tech: "Mega Cap Tech",
  growth: "Growth",
  dividend_value: "Dividend Value",
  blend: "Blend",
  financial: "Financial",
  reit: "REIT",
};

const CompanyTypeBadge = ({ type, reason }) => {
  if (!type) return null;
  const cls = {
    mega_cap_tech: "bg-violet-500/15 text-violet-700 dark:text-violet-400",
    growth: "bg-sky-500/15 text-sky-700 dark:text-sky-400",
    dividend_value: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
    blend: "bg-muted text-muted-foreground",
    financial: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
    reit: "bg-pink-500/15 text-pink-700 dark:text-pink-400",
  }[type] || "bg-muted";
  const badge = (
    <Badge variant="outline" className={`${cls} text-xs uppercase tracking-wide`}>
      {TYPE_LABELS[type] || type}
    </Badge>
  );
  if (!reason) return badge;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild><span>{badge}</span></TooltipTrigger>
        <TooltipContent className="max-w-xs"><p className="text-xs">{reason}</p></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// ---- Stat tile ----
const Stat = ({ label, value, hint, valueClass }) => (
  <div className="rounded-lg border p-3">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`text-lg font-semibold tabular-nums ${valueClass || ""}`}>{value ?? "—"}</div>
    {hint ? <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div> : null}
  </div>
);

// ---- FundRow — single fundamentals table row (mirrors CSE FundRow) ----
const FundRow = ({ label, value }) => (
  <tr className="border-t first:border-t-0">
    <th className="px-4 py-2.5 text-left text-xs uppercase tracking-wider text-muted-foreground font-medium w-1/2">{label}</th>
    <td className="px-4 py-2.5 text-right tabular-nums font-medium">
      {value ?? <span className="text-muted-foreground/60">—</span>}
    </td>
  </tr>
);

// ---- UsSectorComparisonCard — vs Sector card, mirrors the CSE card ----
// Backend returns the same shape so the rendering logic is identical to
// the CSE `SectorComparisonCard` — only formatter differs (USD vs LKR).
const UsSectorComparisonCard = ({ data, symbol }) => {
  if (!data || !data.rows || data.rows.length === 0) {
    return (
      <Card className="border bg-card" data-testid="us-sector-comparison-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-display text-base">vs Sector</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-dashed px-4 py-6 text-center text-xs text-muted-foreground">
            Sector comparison will appear once peer fundamentals are available.
          </div>
        </CardContent>
      </Card>
    );
  }
  const fmt = (key, v) => {
    if (v == null) return "—";
    // dividend_yield, profit_margin, roe come back as decimals (0.27 = 27%)
    if (key === "dividend_yield" || key === "profit_margin" || key === "roe") {
      return `${(Number(v) * 100).toFixed(2)}%`;
    }
    return Number(v).toFixed(2);
  };
  const toneCls = {
    favourable: "text-[hsl(var(--success))]",
    unfavourable: "text-[hsl(var(--destructive))]",
    neutral: "text-muted-foreground",
  };
  const toneIcon = {
    favourable: <TrendingUp className="h-3.5 w-3.5" />,
    unfavourable: <TrendingDown className="h-3.5 w-3.5" />,
    neutral: <ArrowRight className="h-3.5 w-3.5" />,
  };
  return (
    <Card className="border bg-card" data-testid="us-sector-comparison-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="font-display text-base">vs Sector</CardTitle>
          <Badge variant="outline" className="text-[10px]">
            {data.sector} · {data.constituents_count} peer{data.constituents_count === 1 ? "" : "s"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Metric</th>
                <th className="px-3 py-2 text-right">{symbol}</th>
                <th className="px-3 py-2 text-right">Sector median</th>
                <th className="px-3 py-2 text-right">vs Sector</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.key} className="border-t" data-testid={`us-sector-compare-row-${r.key}`}>
                  <td className="px-3 py-2 text-muted-foreground">{r.label}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{fmt(r.key, r.value)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{fmt(r.key, r.sector_median)}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={`inline-flex items-center gap-1 tabular-nums font-medium ${toneCls[r.tone]}`}>
                      {toneIcon[r.tone]}
                      {r.delta_pct == null ? "—" : `${r.delta_pct > 0 ? "+" : ""}${r.delta_pct.toFixed(1)}%`}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[10px] text-muted-foreground">
          Sector medians recompute nightly across {data.constituents_count} S&amp;P 500 peer companies. ✅ favourable · ❌ unfavourable · ➡ within ±10% of sector.
        </p>
      </CardContent>
    </Card>
  );
};

// ---- Layer strip (mini, for Overview tab) ----
// L5 max raised to 15 (now includes earnings beats);
// L6 max lowered to 5 (now uses 12m / 6m return vs S&P 500).
const LAYER_DEFS = [
  { key: "layer1_valuation",    title: "Valuation",  max: 25 },
  { key: "layer2_growth",       title: "Growth",     max: 20 },
  { key: "layer3_quality",      title: "Quality",    max: 20 },
  { key: "layer4_intrinsic",    title: "Intrinsic",  max: 15 },
  { key: "layer5_intelligence", title: "Forward IQ", max: 15 },
  { key: "layer6_technical",    title: "Momentum",   max: 5 },
];

const LayerStrip = ({ score }) => {
  if (!score) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
      {LAYER_DEFS.map((L) => {
        const layer = score[L.key];
        const max = layer?.max_score ?? L.max;
        const pct = layer && max ? Math.max(0, Math.min(100, (layer.score / max) * 100)) : 0;
        return (
          <div key={L.key} className="rounded-lg border p-2.5" data-testid={`us-layer-strip-${L.key}`}>
            <div className="flex items-baseline justify-between">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{L.title}</div>
              <div className="text-xs tabular-nums font-semibold">
                {layer?.score?.toFixed(1) ?? "—"}<span className="text-muted-foreground">/{max}</span>
              </div>
            </div>
            <Progress value={pct} className="h-1.5 mt-1.5" />
          </div>
        );
      })}
    </div>
  );
};

// ---- Score Breakdown — full detail card (Overview tab) ----------------------
// Shows points earned, max points, and the top driver(s) for each layer.
const LAYER_SUBTITLES = {
  layer1_valuation:    "PE / PB / PS / Yield vs sector",
  layer2_growth:       "Revenue & EPS 5y CAGR",
  layer3_quality:      "ROE · Debt/Equity · Liquidity · Margin",
  layer4_intrinsic:    "Fair-value methods (skips N/A) — 100 deeply undervalued / 50 fair / 10 deeply overvalued",
  layer5_intelligence: "Analyst rec · Target upside · Earnings beats",
  layer6_technical:    "12m / 6m return vs S&P 500",
};

const L4_LABELS = {
  graham_number:         "Graham",
  ddm_fair_value:        "DDM",
  dcf_fair_value:        "DCF",
  pe_based_target:       "PE Multiple",
  ev_ebitda_target:      "EV/EBITDA",
  ps_based_target:       "P/S",
  peg_based_target:      "PEG",
  pb_based_target:       "P/B",
  dividend_based_target: "Dividend",
  fcf_yield_target:      "FCF Yield",
  analyst_target:        "Analyst",
};

const summariseLayerDrivers = (layerKey, layer) => {
  // Returns a short list of strings describing the top contributors / drags
  if (!layer || !layer.components) return [];
  const c = layer.components;
  const out = [];

  if (layerKey === "layer1_valuation") {
    Object.entries(c).forEach(([k, v]) => {
      if (!v || typeof v !== "object") return;
      const sm = v.sector_median;
      const val = v.value;
      const sc = v.score;
      const labelMap = { pe: "P/E", pb: "P/B", ps: "P/S", peg: "PEG", dividend_yield: "Yield", revenue_growth: "Rev Growth" };
      const lbl = labelMap[k] || k;
      // Phase 8 FIX 1 — render N/A reason when sub-component skipped (e.g. P/B for mega-cap tech)
      if (sc == null && v.na_reason) {
        out.push(`${lbl} — N/A · ${v.na_reason}`);
        return;
      }
      if (sc == null) return;
      const formatted =
        k === "dividend_yield" || k === "revenue_growth"
          ? fmtFracPct(val)
          : val == null ? "—" : Number(val).toFixed(2);
      const cmp = sm != null
        ? ` vs sector ${k === "dividend_yield" ? fmtFracPct(sm) : Number(sm).toFixed(2)}`
        : "";
      out.push(`${lbl} ${formatted}${cmp} → ${sc}/100`);
    });
  } else if (layerKey === "layer2_growth") {
    Object.entries(c).forEach(([k, v]) => {
      if (!v || typeof v !== "object") return;
      // Phase 8 (Improvement 1) — trend sub-component renders specially
      if (k === "trend") {
        const labelMap = {
          strong_improving: "Strong Improving",
          improving: "Improving",
          stable: "Stable",
          declining: "Declining",
          strong_declining: "Strong Declining",
        };
        const lbl = labelMap[v.label] || v.label || "—";
        out.push(`Trend (vs 5y CAGR + score history): ${lbl} → ${v.points}/${v.max} pts (${v.metrics_evaluated || 0} metrics)`);
        return;
      }
      const lbl = k === "eps_cagr_5y" ? "EPS 5y CAGR" : k === "revenue_cagr_5y" ? "Rev 5y CAGR" : k;
      out.push(`${lbl} ${fmtFracPct(v.value)} → ${v.score}/100`);
    });
  } else if (layerKey === "layer3_quality") {
    const labelMap = { roe: "ROE", debt_to_equity: "D/E", current_ratio: "Current", profit_margin: "Margin" };
    Object.entries(c).forEach(([k, v]) => {
      if (!v || typeof v !== "object") return;
      const lbl = labelMap[k] || k;
      const formatted = (k === "roe" || k === "profit_margin")
        ? fmtFracPct(v.value)
        : v.value == null ? "—" : Number(v.value).toFixed(2);
      out.push(`${lbl} ${formatted} → ${Math.round(v.score ?? 0)}/100`);
    });
  } else if (layerKey === "layer4_intrinsic") {
    Object.entries(c).forEach(([k, v]) => {
      if (!v || typeof v !== "object") return;
      // Phase 8 (Improvement 2) — pe_vs_history sub-component renders specially
      if (k === "pe_vs_history") {
        const cur = v.current_pe;
        const avg = v.pe_5yr_avg;
        const prem = v.premium_pct;
        if (cur == null && avg == null) {
          out.push(`PE-vs-history — N/A (no history yet) → ${v.points}/${v.max} pts`);
        } else {
          const tag = v.neutral_fallback ? " (neutral fallback)" : "";
          out.push(`PE ${cur == null ? "—" : Number(cur).toFixed(1)}x vs 5y avg ${avg == null ? "—" : Number(avg).toFixed(1)}x → ${prem == null ? "—" : `${prem >= 0 ? "+" : ""}${Number(prem).toFixed(1)}%`} → ${v.points}/${v.max} pts${tag}`);
        }
        return;
      }
      const lbl = L4_LABELS[k] || k;
      // Phase 8 FIX 2 — for non-applicable methods, HIDE the dollar value
      // and surface the human-readable reason. Showing "$33.39 → 0/100"
      // for AAPL Graham is misleading because the method is meaningless
      // for asset-light tech.
      if (v.applicable === false) {
        const reason = v.na_reason || "not applicable for this company type";
        out.push(`${lbl} — N/A · ${reason}`);
      } else if (v.score == null) {
        const formatted = v.value == null ? "—" : `$${Number(v.value).toFixed(2)}`;
        out.push(`${lbl} ${formatted} — N/A (data gap)`);
      } else {
        const formatted = v.value == null ? "—" : `$${Number(v.value).toFixed(2)}`;
        out.push(`${lbl} target ${formatted} → ${v.score}/100`);
      }
    });
  } else if (layerKey === "layer5_intelligence") {
    const rk = c.recommendation_key;
    const at = c.analyst_target;
    const eb = c.earnings_beats;
    if (rk) out.push(`Rec: ${rk.value || "—"} (${rk.num_analysts || 0} analysts) → ${rk.points}/${rk.max} pts`);
    if (at) out.push(`Analyst upside ${fmtPct(at.upside_pct)} → ${at.points}/${at.max} pts`);
    if (eb) out.push(`Beats 4Q: ${eb.beat_rate_4q == null ? "—" : `${Math.round(eb.beat_rate_4q * 4)}/4`} · last surprise ${fmtPct(eb.last_surprise_pct)} → ${eb.points}/${eb.max} pts`);
  } else if (layerKey === "layer6_technical") {
    const m12 = c.momentum_12m_vs_sp500;
    const m6  = c.momentum_6m_vs_sp500;
    const sv  = c.sector_verdict;
    if (m12) out.push(`12m vs S&P 500: ${fmtPct(m12.value_pct)} → ${m12.points}/${m12.max} pts`);
    if (m6)  out.push(`6m vs S&P 500: ${fmtPct(m6.value_pct)} → ${m6.points}/${m6.max} pts`);
    if (sv)  out.push(`Sector verdict → ${sv.points}/${sv.max} pts`);
  }
  return out;
};

const ScoreBreakdownDetail = ({ score }) => {
  if (!score) return null;
  const total = LAYER_DEFS.reduce((acc, L) => acc + (score[L.key]?.score || 0), 0);
  const totalMax = LAYER_DEFS.reduce((acc, L) => acc + (score[L.key]?.max_score ?? L.max), 0);

  // ---- Phase 8 (Improvement 1) — quality-trend annotation on L2 -----
  const qTrend = score.quality_trend;
  const trendArrow = {
    strong_improving: { glyph: "↑↑", cls: "text-emerald-600 dark:text-emerald-400 font-bold", label: "Strong Improving" },
    improving:        { glyph: "↑",  cls: "text-emerald-500", label: "Improving" },
    stable:           { glyph: "→",  cls: "text-muted-foreground", label: "Stable" },
    declining:        { glyph: "↓",  cls: "text-amber-600 dark:text-amber-400", label: "Declining" },
    strong_declining: { glyph: "↓↓", cls: "text-rose-600 dark:text-rose-400 font-bold", label: "Strong Declining" },
  }[qTrend];

  return (
    <Card data-testid="us-score-breakdown-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Layers className="h-4 w-4" /> Score Breakdown
        </CardTitle>
        <CardDescription className="text-xs">
          Each layer is graded independently on a 100-pt scale, then weighted into the composite.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {LAYER_DEFS.map((L) => {
          const layer = score[L.key];
          const max = layer?.max_score ?? L.max;
          const pts = layer?.score;
          const pct = layer && max ? Math.max(0, Math.min(100, (pts / max) * 100)) : 0;
          const drivers = summariseLayerDrivers(L.key, layer);
          const showTrend = L.key === "layer2_growth" && trendArrow;
          return (
            <div key={L.key} className="rounded-lg border p-3" data-testid={`us-score-breakdown-${L.key}`}>
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div className="flex items-baseline gap-2">
                  <div className="text-sm font-semibold">{L.title}</div>
                  {showTrend ? (
                    <span
                      className={`text-xs font-medium inline-flex items-center gap-1 ${trendArrow.cls}`}
                      title={`Quality trend: ${trendArrow.label}`}
                      data-testid={`us-l2-trend-${qTrend}`}
                    >
                      {trendArrow.glyph} {trendArrow.label}
                    </span>
                  ) : null}
                  <div className="text-[11px] text-muted-foreground">{LAYER_SUBTITLES[L.key]}</div>
                </div>
                <div className="text-sm tabular-nums font-semibold">
                  {pts == null ? "—" : pts.toFixed(2)}
                  <span className="text-muted-foreground">/{max}</span>
                  <span className="ml-2 text-[11px] font-normal text-muted-foreground">({pct.toFixed(0)}%)</span>
                </div>
              </div>
              <Progress value={pct} className="h-1.5 mt-2" />
              {drivers.length ? (
                <ul className="mt-2 space-y-1 text-[11.5px] text-muted-foreground">
                  {drivers.map((d, i) => (
                    <li key={i} className="font-mono">• {d}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          );
        })}
        <div className="pt-2 mt-1 border-t flex items-baseline justify-between">
          <div className="text-sm font-semibold">Composite Total</div>
          <div className="text-base font-bold tabular-nums">
            {total.toFixed(2)}<span className="text-muted-foreground text-sm">/{totalMax}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ---- Phase 8 (Improvement 3) — Conviction Score card ----
const CONVICTION_THEME = {
  HIGH:        { dots: "●●●", cls: "text-emerald-600 dark:text-emerald-400", border: "border-emerald-500/40", bg: "bg-emerald-500/5" },
  MEDIUM:      { dots: "●●○", cls: "text-amber-600 dark:text-amber-400",     border: "border-amber-500/40",   bg: "bg-amber-500/5" },
  LOW:         { dots: "●○○", cls: "text-muted-foreground",                  border: "border-muted-foreground/30", bg: "bg-muted/20" },
  SPECULATIVE: { dots: "○○○", cls: "text-rose-600 dark:text-rose-400",       border: "border-rose-500/40",    bg: "bg-rose-500/5" },
};

const ConvictionCard = ({ score }) => {
  const conv = score && score.conviction_score != null ? {
    score: score.conviction_score,
    label: score.conviction_label,
    components: score.conviction_components || {},
  } : null;
  if (!conv) return null;
  const theme = CONVICTION_THEME[conv.label] || CONVICTION_THEME.LOW;
  const sa = conv.components.signal_agreement || {};
  const st = conv.components.score_trend || {};
  const dq = conv.components.data_quality || {};
  const ext = conv.components.external_confirmation || {};
  return (
    <Card className={`${theme.border} ${theme.bg}`} data-testid="us-conviction-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between gap-2">
          <span className="flex items-center gap-2"><Sparkles className="h-4 w-4" /> Conviction</span>
          <span className={`font-mono tracking-wide ${theme.cls}`}>{theme.dots}</span>
        </CardTitle>
        <CardDescription className="text-xs flex items-baseline justify-between">
          <span>Confidence in our signal — separate from the 0-100 composite.</span>
          <span className={`text-base font-bold tabular-nums ${theme.cls}`}>
            {Number(conv.score).toFixed(1)}<span className="text-xs text-muted-foreground">/10</span>
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2.5 text-xs">
        <div className={`text-sm font-semibold tracking-wide uppercase ${theme.cls}`}>
          {conv.label} CONVICTION
        </div>
        <Separator />
        <div className="grid grid-cols-1 gap-2">
          <div className="flex items-baseline justify-between" data-testid="us-conviction-signal-agreement">
            <span>Signal agreement</span>
            <span className="tabular-nums">
              {sa.layers_aligned ?? "—"}/6 layers
              <span className="ml-2 text-muted-foreground">{sa.points ?? 0}/{sa.max ?? 3}</span>
            </span>
          </div>
          <div className="flex items-baseline justify-between" data-testid="us-conviction-score-trend">
            <span>Score trend</span>
            <span className="tabular-nums">
              {st.rising_streak >= 1 ? `↑ rising ×${st.rising_streak}` : st.falling_streak >= 1 ? `↓ falling ×${st.falling_streak}` : "stable"}
              <span className="ml-2 text-muted-foreground">{st.points ?? 0}/{st.max ?? 2}</span>
            </span>
          </div>
          <div className="flex items-baseline justify-between" data-testid="us-conviction-data-quality">
            <span>Data quality</span>
            <span className="tabular-nums">
              {dq.populated_fields ?? "—"}/{dq.required_fields ?? "—"} fields · {dq.num_analysts ?? "—"} analysts
              <span className="ml-2 text-muted-foreground">{dq.points ?? 0}/{dq.max ?? 2}</span>
            </span>
          </div>
          <div className="flex items-baseline justify-between" data-testid="us-conviction-external">
            <span>External confirmation</span>
            <span className="tabular-nums">
              {[
                ext.analyst_aligned ? "analyst✓" : null,
                ext.earnings_beat_high ? "beats✓" : null,
                ext.momentum_positive ? "momo✓" : null,
              ].filter(Boolean).join(" · ") || "—"}
              <span className="ml-2 text-muted-foreground">{ext.points ?? 0}/{ext.max ?? 3}</span>
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ---- Phase B — Earnings Call Analysis card ----
// Renders the structured-signals-based management-tone + guidance read.
// Triggers on-demand via the admin endpoint when the user has admin role.
const TONE_TONE_MAP = {
  very_confident: { cls: "text-emerald-700 dark:text-emerald-400", glyph: "📈" },
  confident:      { cls: "text-emerald-600 dark:text-emerald-400", glyph: "📈" },
  neutral:        { cls: "text-muted-foreground",                  glyph: "→" },
  cautious:       { cls: "text-amber-600 dark:text-amber-400",     glyph: "⚠" },
  concerned:      { cls: "text-rose-600 dark:text-rose-400",       glyph: "📉" },
};
const GUIDANCE_TONE_MAP = {
  raised:     { cls: "text-emerald-600 dark:text-emerald-400", label: "RAISED ✅" },
  maintained: { cls: "text-muted-foreground",                  label: "MAINTAINED" },
  lowered:    { cls: "text-rose-600 dark:text-rose-400",       label: "LOWERED ⚠" },
  withdrawn:  { cls: "text-rose-700 dark:text-rose-500",       label: "WITHDRAWN ⚠⚠" },
  unknown:    { cls: "text-muted-foreground",                  label: "—" },
};

const EarningsCallCard = ({ symbol, isAdmin }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const load = useCallback(async () => {
    if (!symbol) return;
    setLoading(true); setErrorMsg(null);
    try {
      const r = await apiGet(`/us-stocks/${encodeURIComponent(symbol)}/earnings-call`);
      setData(r);
    } catch (e) {
      if (e?.response?.status === 404) {
        setData(null);  // simply not generated yet
      } else {
        setErrorMsg(e?.response?.data?.detail || e.message || "Failed to load");
      }
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  useEffect(() => { load(); }, [load]);

  const runAnalysis = useCallback(async () => {
    if (!symbol || running) return;
    setRunning(true);
    const t = toast.loading(`Running earnings-call analysis for ${symbol}…`);
    try {
      const r = await apiPost(`/admin/us-earnings-call/run/${encodeURIComponent(symbol)}?force=true`);
      setData(r);
      toast.success(`Analysed ${symbol} — tone ${r.management_tone_score}/10`, { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Analysis failed", { id: t });
    } finally {
      setRunning(false);
    }
  }, [symbol, running]);

  if (loading) {
    return <Skeleton className="h-32 rounded-lg" data-testid="us-earnings-call-card-loading" />;
  }
  if (errorMsg) {
    return (
      <Card data-testid="us-earnings-call-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm">📞 Earnings Call Analysis</CardTitle></CardHeader>
        <CardContent><p className="text-xs text-rose-500">{errorMsg}</p></CardContent>
      </Card>
    );
  }
  if (!data) {
    return (
      <Card data-testid="us-earnings-call-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">📞 Earnings Call Analysis</CardTitle>
          <CardDescription className="text-[11px]">
            AI-synthesized management tone &amp; guidance from the most recent reported quarter.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-muted-foreground italic">
            No analysis yet for {symbol}. {isAdmin ? "Click below to run one." : "An admin can generate one."}
          </p>
          {isAdmin ? (
            <Button
              size="sm"
              variant="outline"
              onClick={runAnalysis}
              disabled={running}
              data-testid="us-earnings-call-run-btn"
            >
              {running ? "Analysing…" : `Analyse ${symbol}`}
            </Button>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  const toneMeta = TONE_TONE_MAP[(data.management_tone_label || "").toLowerCase()] || TONE_TONE_MAP.neutral;
  const guideMeta = GUIDANCE_TONE_MAP[(data.guidance_direction || "unknown").toLowerCase()] || GUIDANCE_TONE_MAP.unknown;
  const date = data.earnings_date ? data.earnings_date.split("T")[0] : "—";

  return (
    <Card data-testid="us-earnings-call-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span>📞 Earnings Call Analysis</span>
          {isAdmin ? (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-[11px]"
              onClick={runAnalysis}
              disabled={running}
              data-testid="us-earnings-call-rerun-btn"
            >
              {running ? "…" : "Re-run"}
            </Button>
          ) : null}
        </CardTitle>
        <CardDescription className="text-[11px]">
          {data.quarter} · {date} · {data.transcript_available ? "transcript-based" : "structured-signals analysis"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-2.5">
          <div className="rounded-md border p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Management Tone</div>
            <div className={`text-base font-semibold ${toneMeta.cls}`}>
              {toneMeta.glyph} {data.management_tone_score == null ? "—" : `${data.management_tone_score}/10`}
              <span className="ml-2 text-[11px] uppercase">{(data.management_tone_label || "").replace("_", " ")}</span>
            </div>
          </div>
          <div className="rounded-md border p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Guidance</div>
            <div className={`text-base font-semibold ${guideMeta.cls}`}>{guideMeta.label}</div>
            {data.guidance_basis ? (
              <div className="text-[10.5px] text-muted-foreground mt-0.5">{data.guidance_basis}</div>
            ) : null}
          </div>
        </div>

        {data.summary ? (
          <div className="rounded-md border p-2.5 bg-muted/30">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Summary</div>
            <div className="text-[12.5px] leading-relaxed">{data.summary}</div>
          </div>
        ) : null}

        {(data.key_risks_new && data.key_risks_new.length) || (data.key_opportunities && data.key_opportunities.length) ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
            <div className="rounded-md border p-2.5">
              <div className="text-[10px] uppercase tracking-wider text-rose-600 dark:text-rose-400 mb-1">Key Risks</div>
              {(data.key_risks_new || []).length ? (
                <ul className="text-[11.5px] space-y-1">
                  {(data.key_risks_new || []).map((r, i) => <li key={i}>• {r}</li>)}
                </ul>
              ) : <p className="text-[11px] text-muted-foreground italic">None flagged.</p>}
            </div>
            <div className="rounded-md border p-2.5">
              <div className="text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-1">Opportunities</div>
              {(data.key_opportunities || []).length ? (
                <ul className="text-[11.5px] space-y-1">
                  {(data.key_opportunities || []).map((r, i) => <li key={i}>• {r}</li>)}
                </ul>
              ) : <p className="text-[11px] text-muted-foreground italic">None flagged.</p>}
            </div>
          </div>
        ) : null}

        <div className="flex items-center justify-between pt-1 text-[11px] text-muted-foreground">
          <span>Analyst sentiment: <span className="font-medium">{data.analyst_sentiment || "—"}</span></span>
          <span>L5 impact: <span className={`font-semibold ${(data.impact_on_l5_score || 0) > 0 ? "text-emerald-600" : (data.impact_on_l5_score || 0) < 0 ? "text-rose-600" : ""}`}>{(data.impact_on_l5_score || 0) >= 0 ? "+" : ""}{data.impact_on_l5_score ?? 0}pts</span></span>
        </div>
        {data.confidence_note ? (
          <p className="text-[10.5px] text-muted-foreground italic border-t pt-1.5">{data.confidence_note}</p>
        ) : null}
      </CardContent>
    </Card>
  );
};


const HistoricalValuationCard = ({ score, fund }) => {
  const cur = score?.pe_ratio;
  const avg = score?.pe_5yr_avg ?? fund?.pe_5yr_avg;
  if (cur == null && avg == null) return null;
  const premium = score?.pe_vs_history_pct;
  const tone =
    premium == null ? "neutral"
    : premium <= -10 ? "good"
    : premium <= 10  ? "neutral"
    : premium <= 20  ? "warn"
    : "bad";
  const toneCls = {
    good: "text-emerald-600 dark:text-emerald-400",
    neutral: "text-muted-foreground",
    warn: "text-amber-600 dark:text-amber-400",
    bad: "text-rose-600 dark:text-rose-400",
  }[tone];
  const toneTag = {
    good: "discount to history ✅",
    neutral: "in line with history",
    warn: "premium to history ⚠",
    bad: "extreme premium to history ⚠⚠",
  }[tone];
  return (
    <Card data-testid="us-historical-valuation-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Calculator className="h-4 w-4" /> vs Own History
        </CardTitle>
        <CardDescription className="text-[11px]">
          Current PE compared to the stock&apos;s 5-year average (approx.).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2.5 text-sm">
        <div className="flex items-center justify-between rounded-md border p-2.5">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Current PE</div>
            <div className="text-base font-semibold tabular-nums">{cur == null ? "—" : Number(cur).toFixed(2)}x</div>
          </div>
        </div>
        <div className="flex items-center justify-between rounded-md border p-2.5">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">5-year average PE</div>
            <div className="text-base font-semibold tabular-nums">{avg == null ? "—" : Number(avg).toFixed(2)}x</div>
          </div>
        </div>
        <div className="flex items-center justify-between rounded-md border p-2.5">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Premium / discount to history</div>
            <div className={`text-base font-semibold tabular-nums ${toneCls}`}>
              {premium == null ? "—" : `${premium >= 0 ? "+" : ""}${Number(premium).toFixed(1)}%`}
              <span className="ml-2 text-[10px] uppercase tracking-wide">{toneTag}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ---- Momentum vs S&P 500 (Overview tab) ------------------------------------
const MomentumStat = ({ label, valuePct, points, maxPts }) => {
  const ok = valuePct != null && valuePct > 0;
  const Icon = ok ? TrendingUp : TrendingDown;
  const color = ok ? "text-emerald-600 dark:text-emerald-400" : valuePct == null ? "text-muted-foreground" : "text-rose-600 dark:text-rose-400";
  return (
    <div className="flex items-center justify-between rounded-md border p-2.5">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className={`text-sm font-semibold tabular-nums flex items-center gap-1.5 ${color}`}>
          <Icon className="h-3.5 w-3.5" />
          {valuePct == null ? "—" : `${valuePct >= 0 ? "+" : ""}${Number(valuePct).toFixed(2)}%`}
        </div>
      </div>
      <div className="text-xs tabular-nums text-muted-foreground">
        {points == null ? "—" : points}<span>/{maxPts}</span> pts
      </div>
    </div>
  );
};

const MomentumVsSp500Card = ({ score }) => {
  const l6 = score?.layer6_technical;
  const m12 = l6?.components?.momentum_12m_vs_sp500;
  const m6  = l6?.components?.momentum_6m_vs_sp500;
  if (!m12 && !m6) return null;
  return (
    <Card data-testid="us-momentum-vs-sp500-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <BarChart3 className="h-4 w-4" /> Momentum vs S&amp;P 500
        </CardTitle>
        <CardDescription className="text-[11px]">
          Total return relative to the S&amp;P 500 (^GSPC) over the same window.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2.5">
        <MomentumStat label="12-month relative return" valuePct={m12?.value_pct} points={m12?.points} maxPts={m12?.max ?? 2} />
        <MomentumStat label="6-month relative return"  valuePct={m6?.value_pct}  points={m6?.points}  maxPts={m6?.max ?? 1} />
      </CardContent>
    </Card>
  );
};

// ---- Earnings Quality (Fundamentals tab) -----------------------------------
const EarningsQualityCard = ({ fund, score }) => {
  // Pull primarily from fundamentals; fall back to score doc earnings_beats sub-component.
  const beat = fund?.earnings_beat_rate_4q;
  const surprise = fund?.last_earnings_surprise_pct;
  const ebPts = score?.layer5_intelligence?.components?.earnings_beats;

  if (beat == null && surprise == null) {
    return (
      <Card data-testid="us-earnings-quality-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> Earnings Quality
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground italic">
            Earnings history not yet available. Will populate on the next forced fundamentals refresh.
          </p>
        </CardContent>
      </Card>
    );
  }

  const beatsOutOf4 = beat == null ? null : Math.round(beat * 4);
  // Trend logic: combine beat rate + surprise direction
  let trendLabel = "Stable";
  let TrendIcon = ArrowRight;
  let trendColor = "text-muted-foreground";
  if (beat != null) {
    if (beat >= 0.75 && (surprise ?? 0) >= 0) {
      trendLabel = "Improving";
      TrendIcon = TrendingUp;
      trendColor = "text-emerald-600 dark:text-emerald-400";
    } else if (beat <= 0.25 || (surprise != null && surprise <= -10)) {
      trendLabel = "Declining";
      TrendIcon = TrendingDown;
      trendColor = "text-rose-600 dark:text-rose-400";
    }
  }

  return (
    <Card data-testid="us-earnings-quality-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Sparkles className="h-4 w-4" /> Earnings Quality
        </CardTitle>
        <CardDescription className="text-[11px]">
          Last 4 quarters of reported vs estimated EPS (yfinance).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2.5 text-sm">
        <div className="flex items-center justify-between rounded-md border p-2.5">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Beat rate (last 4 quarters)</div>
            <div className="text-base font-semibold tabular-nums">
              {beatsOutOf4 == null ? "—" : `${beatsOutOf4}/4`}
              {beat != null ? <span className="ml-2 text-[11px] text-muted-foreground">({Math.round(beat * 100)}%)</span> : null}
            </div>
          </div>
          {ebPts ? (
            <div className="text-xs tabular-nums text-muted-foreground">
              {ebPts.points ?? "—"}/<span>{ebPts.max ?? 3}</span> pts
            </div>
          ) : null}
        </div>
        <div className="flex items-center justify-between rounded-md border p-2.5">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Last quarter surprise</div>
            <div className={`text-base font-semibold tabular-nums ${
              surprise == null ? "" : surprise > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"
            }`}>
              {surprise == null ? "—" : `${surprise >= 0 ? "+" : ""}${Number(surprise).toFixed(2)}%`}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between rounded-md border p-2.5">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Earnings trend</div>
            <div className={`text-sm font-semibold flex items-center gap-1.5 ${trendColor}`}>
              <TrendIcon className="h-3.5 w-3.5" /> {trendLabel}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ---- Valuation Method row (with N/A tooltip) ----
const METHOD_LABEL_MAP = {
  pe_based: "PE Multiple",
  ev_ebitda: "EV/EBITDA",
  ps_based: "P/S Ratio",
  peg_based: "PEG Ratio",
  pb_based: "P/B (Book Value)",
  dividend_based: "Dividend Yield",
  fcf_yield: "FCF Yield",
  graham: "Graham Number",
  ddm: "Dividend Discount Model",
  dcf: "Discounted Cash Flow",
  analyst: "Analyst Consensus",
};

// Render order for the Valuation Methods table — keeps the most
// fundamentally important methods at the top, pure-N/A reference
// methods at the bottom. Methods not in this list are appended.
const METHOD_ORDER = [
  "pe_based", "pb_based", "ev_ebitda", "ev_revenue", "ps_based", "peg_based",
  "fcf_yield", "dividend_based", "graham", "ddm", "dcf", "analyst",
];

const ValuationMethodsTable = ({ score }) => {
  const methods = score?.valuation_methods || {};
  const ordered = useMemo(() => {
    const known = METHOD_ORDER.filter((k) => methods[k]);
    const extras = Object.keys(methods).filter((k) => !METHOD_ORDER.includes(k));
    return [...known, ...extras];
  }, [methods]);

  if (!ordered.length) {
    return (
      <div className="p-8 text-sm text-center text-muted-foreground">
        No valuation methods stored for this stock.
      </div>
    );
  }

  const coverageWarning = score?.coverage_warning;
  const r40Score = score?.rule_of_40_score;
  const r40Adj = score?.rule_of_40_adjustment;

  return (
    <TooltipProvider delayDuration={150}>
      {coverageWarning ? (
        <div
          className="mb-3 rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700/60 px-3 py-2 text-xs text-amber-900 dark:text-amber-100"
          data-testid="us-detail-coverage-warning"
        >
          <span className="font-semibold mr-1">⚠ Limited valuation coverage:</span>
          {coverageWarning}
        </div>
      ) : null}
      {r40Score != null ? (
        <div
          className="mb-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs"
          data-testid="us-detail-rule-of-40"
        >
          <span className="font-semibold">Rule of 40 score:</span>{" "}
          <span className="tabular-nums">{r40Score.toFixed(1)}</span>{" "}
          <span className="text-muted-foreground">
            (revenue growth + profit margin %).{" "}
            {r40Adj > 1
              ? `Premium quality — base target uplifted ${Math.round((r40Adj - 1) * 100)}%.`
              : r40Adj < 1
                ? `Below-bar — base target discounted ${Math.round((1 - r40Adj) * 100)}%.`
                : "On-bar — no blend adjustment."}
          </span>
        </div>
      ) : null}
      <div className="rounded-lg border overflow-hidden" data-testid="us-detail-valuation-methods-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Method</TableHead>
              <TableHead className="text-right">Value</TableHead>
              <TableHead className="text-right">Weight</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden md:table-cell">Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ordered.map((key) => {
              const m = methods[key];
              const isUsed = m.used_weight != null && m.value != null && m.value > 0;
              const hasValue = m.value != null && m.value > 0;
              const isApplicable = !!m.applicable;
              return (
                <TableRow
                  key={key}
                  data-testid={`us-method-row-${key}`}
                  className={!isUsed ? "opacity-80" : ""}
                >
                  <TableCell className="font-medium">
                    {METHOD_LABEL_MAP[key] || m.label || key}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {hasValue ? fmtUsd(m.value) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {isUsed ? `${Math.round(m.used_weight * 100)}%` : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    {isUsed ? (
                      <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 text-[10px]">
                        ✅ Used
                      </Badge>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground cursor-help" data-testid={`us-method-na-${key}`}>
                            <span className="text-base leading-none">⚫</span>
                            N/A
                            <Info className="h-3 w-3" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-sm" data-testid={`us-method-tooltip-${key}`}>
                          <p className="text-xs">{m.na_reason || "Not applicable for this company type."}</p>
                          {!isApplicable ? (
                            <p className="text-[10px] text-muted-foreground mt-1">
                              Reason: methodology — does not apply to {TYPE_LABELS[score?.company_type] || "this company type"}.
                            </p>
                          ) : (
                            <p className="text-[10px] text-muted-foreground mt-1">Reason: missing input data.</p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-xs text-muted-foreground max-w-md">
                    {m.caveat ? (
                      <span className="text-amber-700 dark:text-amber-400 flex items-start gap-1">
                        <span aria-hidden="true">⚠</span>
                        <span className="break-words">{m.caveat}</span>
                      </span>
                    ) : !isUsed && m.na_reason ? (
                      <span className="truncate block">{m.na_reason}</span>
                    ) : isUsed ? (
                      "Applied to base case blend"
                    ) : ""}
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow className="bg-muted/40 font-semibold">
              <TableCell>Base Case Target</TableCell>
              <TableCell className="text-right tabular-nums">
                {fmtUsd(score?.base_target_price)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {score?.total_weight_applied != null
                  ? `${Math.round((score.total_weight_applied || 0) * 100)}%`
                  : "—"}
              </TableCell>
              <TableCell colSpan={2} className="text-xs font-normal text-muted-foreground">
                Weighted blend of applicable methods
                {score?.total_weight_applied && score.total_weight_applied < 0.99
                  ? ` (renormalised — coverage: ${Math.round((score.total_weight_applied || 0) * 100)}%)`
                  : ""}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
};

// ---- Price chart ----
const PriceChart = ({ prices }) => {
  if (!prices?.length) {
    return <div className="p-8 text-center text-sm text-muted-foreground">No price history.</div>;
  }
  return (
    <div className="h-72 w-full" data-testid="us-detail-price-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={prices} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <defs>
            <linearGradient id="us-price-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11 }}
            tickFormatter={(d) => {
              try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
              catch { return d; }
            }}
            minTickGap={36}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            domain={["auto", "auto"]}
            tickFormatter={(v) => `$${Number(v).toFixed(0)}`}
          />
          <RTooltip
            contentStyle={{ borderRadius: 8, fontSize: 12 }}
            formatter={(v) => [fmtUsd(v), "Close"]}
            labelFormatter={(d) => fmtDate(d)}
          />
          <Area
            type="monotone"
            dataKey="close"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            fill="url(#us-price-grad)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// ---- Page ----
const UsStockDetailPage = () => {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const [data, setData] = useState(null);
  const [peers, setPeers] = useState([]);
  const [sectorCompare, setSectorCompare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedSummary, setExpandedSummary] = useState(false);
  // Sync activeTab with `?tab=` URL param so deep links from the
  // screener (e.g. /us-stocks/AAPL?tab=deep-ai) open the right tab.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const t = searchParams.get("tab");
    const allowed = ["overview", "fundamentals", "prices", "peers", "valuation", "deep-ai"];
    if (t && allowed.includes(t)) setActiveTab(t);
  }, [searchParams]);

  // Gating
  useEffect(() => {
    if (!user) return;
    if (isAdmin) return;
    if (!user.us_stocks_enabled) {
      toast.error("US Stocks not enabled for this account.");
      navigate("/stocks", { replace: true });
    }
  }, [user, isAdmin, navigate]);

  const load = useCallback(async () => {
    if (!symbol) return;
    setLoading(true); setError(null);
    try {
      const [full, peerRes, sectorRes] = await Promise.all([
        apiGet(`/us-stocks/${encodeURIComponent(symbol)}/full?history_days=252`),
        apiGet(`/us-stocks/${encodeURIComponent(symbol)}/peers?limit=5`).catch(() => ({ peers: [] })),
        apiGet(`/us-stocks/${encodeURIComponent(symbol)}/sector-comparison`).catch(() => null),
      ]);
      setData(full);
      setPeers(peerRes?.peers || []);
      setSectorCompare(sectorRes || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load stock");
      setData(null); setPeers([]); setSectorCompare(null);
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  useEffect(() => { load(); }, [load]);

  // ---- Admin refresh actions (visible only when isAdmin) ----
  const [refreshing, setRefreshing] = useState(false);
  const runRefresh = useCallback(
    async (kind) => {
      if (!symbol || refreshing) return;
      setRefreshing(true);
      const t = toast.loading(`Refreshing ${kind} for ${symbol}...`);
      try {
        let res;
        if (kind === "fundamentals") {
          res = await apiPost(`/admin/us-fundamentals/refresh/${encodeURIComponent(symbol)}`);
          toast.success(`${symbol} fundamentals refreshed`, {
            id: t,
            description: `Source: ${res?.source || "?"} · PE ${res?.pe_ratio?.toFixed?.(1) ?? "—"}`,
          });
        } else if (kind === "prices") {
          res = await apiPost(`/admin/us-stocks/${encodeURIComponent(symbol)}/refresh-prices?years=2`);
          toast.success(`${symbol} prices refreshed`, {
            id: t,
            description: `${res?.rows_written ?? "?"} rows · ${res?.earliest} → ${res?.latest}`,
          });
        } else if (kind === "score") {
          res = await apiPost(`/admin/us-stocks/${encodeURIComponent(symbol)}/recompute`);
          toast.success(`${symbol} score recomputed`, {
            id: t,
            description: `${res?.composite_score?.toFixed?.(1)} · ${res?.signal}`,
          });
        } else if (kind === "all") {
          res = await apiPost(`/admin/us-stocks/${encodeURIComponent(symbol)}/refresh-all?years=2`);
          const sc = res?.score || {};
          if (res?.overall_status === "ok") {
            toast.success(`${symbol} refreshed end-to-end`, {
              id: t,
              description: `${sc.composite_score?.toFixed?.(1)} · ${sc.signal} · ${sc.valuation_status || ""}`,
            });
          } else {
            toast.warning(`${symbol} refreshed with warnings`, {
              id: t,
              description: `Fund ${res?.fundamentals?.status} · Prices ${res?.prices?.status} · Score ${sc.status}`,
            });
          }
        }
        await load(); // re-fetch the page so the new score / price renders
      } catch (e) {
        toast.error(`Refresh failed`, {
          id: t,
          description: e?.response?.data?.detail || e?.message || "Unknown error",
        });
      } finally {
        setRefreshing(false);
      }
    },
    [symbol, refreshing, load],
  );

  // 52-week stats from price history — must be declared at top level
  // (not after early returns) to keep React's hook order stable across
  // loading / loaded renders.
  const prices = data?.prices || [];
  const fiftyTwoWeek = useMemo(() => {
    if (!prices?.length) return { high: null, low: null };
    const closes = prices.map((p) => Number(p.close)).filter((n) => !isNaN(n));
    if (!closes.length) return { high: null, low: null };
    return { high: Math.max(...closes), low: Math.min(...closes) };
  }, [prices]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="us-detail-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  if (error || !data?.composite_score) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/us-stocks")}>
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
        </Button>
        <Card>
          <CardContent className="p-12 text-center">
            <AlertCircle className="h-10 w-10 mx-auto text-destructive mb-3" />
            <div className="text-base font-semibold mb-1">Could not load {symbol}</div>
            <div className="text-sm text-muted-foreground">{error || "No score data is available for this ticker."}</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const score = data.composite_score || {};
  const fund = data.fundamentals || {};
  const company = data.company || {};

  const upClass =
    score.upside_pct > 0 ? "text-emerald-600 dark:text-emerald-400" :
    score.upside_pct < 0 ? "text-destructive" : "text-muted-foreground";
  const UpsideArrow = score.upside_pct > 0 ? ArrowUpRight : ArrowDownRight;

  return (
    <div className="space-y-5" data-testid="us-detail-page">
      {/* Back / breadcrumb */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate("/us-stocks")} data-testid="us-detail-back">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> All US Stocks
        </Button>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <div className="hidden sm:flex items-center gap-2">
            <Globe2 className="h-4 w-4" />
            <span>NASDAQ / NYSE — USD</span>
          </div>
          {isAdmin ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={refreshing}
                  data-testid="us-detail-admin-refresh-trigger"
                >
                  {refreshing ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Refresh Data
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Admin actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => runRefresh("all")}
                  disabled={refreshing}
                  data-testid="us-detail-admin-refresh-all"
                >
                  <Zap className="h-3.5 w-3.5 mr-2" /> Refresh All
                  <span className="ml-auto text-[10px] text-muted-foreground">~10s</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => runRefresh("fundamentals")}
                  disabled={refreshing}
                  data-testid="us-detail-admin-refresh-fund"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-2" /> Refresh Fundamentals
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => runRefresh("prices")}
                  disabled={refreshing}
                  data-testid="us-detail-admin-refresh-prices"
                >
                  <RefreshCcw className="h-3.5 w-3.5 mr-2" /> Refresh Prices
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => runRefresh("score")}
                  disabled={refreshing}
                  data-testid="us-detail-admin-refresh-score"
                >
                  <Calculator className="h-3.5 w-3.5 mr-2" /> Recompute Score
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      {/* Header card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
            <div className="space-y-2">
              <div className="flex flex-wrap items-baseline gap-3">
                <div className="font-mono text-3xl font-bold tracking-tight" data-testid="us-detail-symbol">
                  {score.symbol || symbol}
                </div>
                <div className="text-xl text-muted-foreground" data-testid="us-detail-company-name">
                  {company.company_name || score.company_name}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                {company.sector ? <Badge variant="outline">{company.sector}</Badge> : null}
                {company.industry ? <span>· {company.industry}</span> : null}
                <CompanyTypeBadge type={score.company_type} reason={score.company_type_reason} />
                {(company.index_membership || []).map((m) => (
                  <Badge key={m} variant="secondary" className="text-[10px]">{m}</Badge>
                ))}
              </div>
              {company.website ? (
                <a
                  href={company.website}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  {company.website.replace(/^https?:\/\//, "")} <ExternalLink className="h-3 w-3" />
                </a>
              ) : null}
            </div>

            <div className="flex flex-col items-start lg:items-end gap-2">
              <div className="text-3xl font-bold tabular-nums" data-testid="us-detail-price">
                {fmtUsd(score.latest_close)}
              </div>
              <div className="flex items-center gap-2">
                <SignalBadge signal={score.signal} large />
                <ValuationBadge status={score.valuation_status} />
              </div>
              <div className={`text-sm tabular-nums inline-flex items-center gap-1 ${upClass}`}>
                <UpsideArrow className="h-4 w-4" />
                {fmtPct(score.upside_pct)} upside vs base target {fmtUsd(score.base_target_price)}
              </div>
            </div>
          </div>

          {/* Quick stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
            <Stat
              label="Composite Score"
              value={<span data-testid="us-detail-score">{score.composite_score?.toFixed(1) ?? "—"}<span className="text-sm text-muted-foreground"> /100</span></span>}
              hint={`${score.signal?.replace("_", " ") || "—"} signal`}
            />
            <Stat
              label="Market Cap"
              value={fmtUsd(score.market_cap, { compact: true })}
              hint={score.market_cap_class === "mega_cap" ? "Mega cap" : "Large/Mid cap"}
            />
            <Stat
              label="P/E"
              value={fmtNum(score.pe_ratio)}
              hint={score.pb_ratio != null ? `P/B ${fmtNum(score.pb_ratio)}` : null}
            />
            <Stat
              label="Dividend Yield"
              value={score.dividend_yield_pct == null ? "—" : fmtPct(score.dividend_yield_pct, 2)}
              hint={fund.payout_ratio != null ? `Payout ${fmtFracPct(fund.payout_ratio)}` : null}
            />
          </div>
          <div className="mt-3">
            <DataSourceNote testId="us-detail-data-note" />
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4" data-testid="us-detail-tabs">
        <TabsList className="grid grid-cols-6 w-full max-w-4xl">
          <TabsTrigger value="overview" data-testid="us-detail-tab-overview"><Layers className="h-3.5 w-3.5 mr-1.5" /> Overview</TabsTrigger>
          <TabsTrigger value="fundamentals" data-testid="us-detail-tab-fundamentals"><Building2 className="h-3.5 w-3.5 mr-1.5" /> Fundamentals</TabsTrigger>
          <TabsTrigger value="prices" data-testid="us-detail-tab-prices"><LineChartIcon className="h-3.5 w-3.5 mr-1.5" /> Price History</TabsTrigger>
          <TabsTrigger value="peers" data-testid="us-detail-tab-peers"><Users className="h-3.5 w-3.5 mr-1.5" /> Peers</TabsTrigger>
          <TabsTrigger value="valuation" data-testid="us-detail-tab-valuation"><Calculator className="h-3.5 w-3.5 mr-1.5" /> Valuation</TabsTrigger>
          <TabsTrigger value="deep-ai" data-testid="us-detail-tab-deep-ai"><Bot className="h-3.5 w-3.5 mr-1.5" /> Deep AI</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-5">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-4 w-4" /> 6-Layer Composite
              </CardTitle>
              <CardDescription className="text-xs">
                Each layer is graded independently then weighted. See <em>Score Breakdown</em> below for the full detail.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <LayerStrip score={score} />
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4">
            <ScoreBreakdownDetail score={score} />
            <div className="space-y-4">
              <ConvictionCard score={score} />
              <EarningsCallCard symbol={symbol} isAdmin={isAdmin} />
              <MomentumVsSp500Card score={score} />
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-sm">Recent Price (1Y)</CardTitle></CardHeader>
                <CardContent>
                  <PriceChart prices={prices} />
                  <div className="grid grid-cols-2 mt-3 gap-3 text-xs text-muted-foreground">
                    <div>52w High: <span className="text-foreground tabular-nums">{fmtUsd(fiftyTwoWeek.high)}</span></div>
                    <div>52w Low: <span className="text-foreground tabular-nums">{fmtUsd(fiftyTwoWeek.low)}</span></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <Card data-testid="us-detail-company-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  About {score.symbol || symbol}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {(() => {
                  const summary = company.business_summary || company.description || "";
                  if (!summary) {
                    return (
                      <p className="text-muted-foreground italic">
                        Company description not yet available. It will populate on the next fundamentals refresh.
                      </p>
                    );
                  }
                  const isLong = summary.length > 480;
                  const visible = expandedSummary || !isLong ? summary : summary.slice(0, 480) + "…";
                  return (
                    <>
                      <p
                        className="text-muted-foreground leading-relaxed whitespace-pre-line"
                        data-testid="us-detail-business-summary"
                      >
                        {visible}
                      </p>
                      {isLong ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => setExpandedSummary((v) => !v)}
                          data-testid="us-detail-business-summary-toggle"
                        >
                          {expandedSummary ? "Show less" : "Read more"}
                        </Button>
                      ) : null}
                    </>
                  );
                })()}
                <Separator />
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-muted-foreground">Sector</div>
                    <div className="font-medium">{company.sector || "—"}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Industry</div>
                    <div className="font-medium">{company.industry || company.sub_industry || "—"}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Mode</div>
                    <div className="font-medium">{score.growth_or_value || "—"}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Type</div>
                    <div className="font-medium">{TYPE_LABELS[score.company_type] || score.company_type || "—"}</div>
                  </div>
                  {company.headquarters ? (
                    <div className="col-span-2">
                      <div className="text-muted-foreground">Headquarters</div>
                      <div className="font-medium">{company.headquarters}</div>
                    </div>
                  ) : null}
                  <div>
                    <div className="text-muted-foreground">Exchange</div>
                    <div className="font-medium">{company.exchange || "—"}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Currency</div>
                    <div className="font-medium">{company.currency || score.currency || "USD"}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
        </TabsContent>
        <TabsContent value="fundamentals" className="mt-4 focus-visible:outline-none space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-6">
            <Card className="border bg-card">
              <CardHeader>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="font-display">Fundamentals</CardTitle>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Sourced from yfinance · As of {fmtDate(fund.as_of_date)}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto rounded-md border" data-testid="us-fundamentals-table">
                  <table className="w-full text-sm">
                    <tbody>
                      <FundRow label="P/E Ratio (TTM)" value={fund.pe_ratio == null ? null : fmtNum(fund.pe_ratio)} />
                      <FundRow label="Forward P/E" value={fund.forward_pe == null ? null : fmtNum(fund.forward_pe)} />
                      <FundRow label="P/B Ratio" value={fund.pb_ratio == null ? null : fmtNum(fund.pb_ratio)} />
                      <FundRow label="P/S Ratio" value={fund.ps_ratio == null ? null : fmtNum(fund.ps_ratio)} />
                      <FundRow label="PEG Ratio" value={fund.peg_ratio == null ? null : fmtNum(fund.peg_ratio)} />
                      <FundRow label="EV / EBITDA" value={fund.ev_ebitda == null ? null : fmtNum(fund.ev_ebitda)} />
                      <FundRow label="EPS (TTM)" value={fund.eps_ttm == null ? null : fmtUsd(fund.eps_ttm)} />
                      <FundRow label="ROE" value={fund.roe == null ? null : fmtFracPct(fund.roe)} />
                      <FundRow label="ROIC" value={fund.roic == null ? null : fmtFracPct(fund.roic)} />
                      <FundRow label="Profit Margin" value={fund.profit_margin == null ? null : fmtFracPct(fund.profit_margin)} />
                      <FundRow label="Dividend Yield" value={fund.dividend_yield == null ? null : fmtFracPct(fund.dividend_yield)} />
                      <FundRow label="Payout Ratio" value={fund.payout_ratio == null ? null : fmtFracPct(fund.payout_ratio)} />
                      <FundRow label="Debt / Equity" value={fund.debt_to_equity == null ? null : fmtNum(fund.debt_to_equity)} />
                      <FundRow label="Current Ratio" value={fund.current_ratio == null ? null : fmtNum(fund.current_ratio)} />
                      <FundRow label="Revenue (TTM)" value={fund.revenue_ttm == null ? null : fmtUsd(fund.revenue_ttm, { compact: true })} />
                      <FundRow label="Revenue 5y CAGR" value={fund.revenue_cagr_5y == null ? null : fmtFracPct(fund.revenue_cagr_5y)} />
                      <FundRow label="EPS 5y CAGR" value={fund.eps_cagr_5y == null ? null : fmtFracPct(fund.eps_cagr_5y)} />
                      <FundRow label="Free Cash Flow (TTM)" value={fund.free_cashflow_ttm == null ? null : fmtUsd(fund.free_cashflow_ttm, { compact: true })} />
                      <FundRow label="Beta" value={fund.beta == null ? null : fmtNum(fund.beta, 2)} />
                      <FundRow label="Market Cap" value={fund.market_cap == null ? null : fmtUsd(fund.market_cap, { compact: true })} />
                      <FundRow label="Analyst Target" value={
                        fund.target_mean_price == null
                          ? null
                          : <span>
                              {fmtUsd(fund.target_mean_price)}
                              {fund.recommendation_key && (
                                <span className="ml-2 text-[11px] text-muted-foreground uppercase">
                                  {fund.recommendation_key} · {fund.num_analysts || 0}
                                </span>
                              )}
                            </span>
                      } />
                    </tbody>
                  </table>
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  Last updated {fund.as_of_date ? fmtDate(fund.as_of_date) : "—"}
                </p>
              </CardContent>
            </Card>

            {/* vs Sector card — mirrors CSE SectorComparisonCard, plus Earnings Quality + Historical Valuation */}
            <div className="space-y-6">
              <UsSectorComparisonCard data={sectorCompare} symbol={symbol} />
              <HistoricalValuationCard score={score} fund={fund} />
              <EarningsQualityCard fund={fund} score={score} />
            </div>
          </div>
        </TabsContent>

        {/* Price History */}
        <TabsContent value="prices" className="space-y-4">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Price history (last {prices.length} sessions)</CardTitle></CardHeader>
            <CardContent>
              <PriceChart prices={prices} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm">Recent sessions</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Open</TableHead>
                      <TableHead className="text-right">High</TableHead>
                      <TableHead className="text-right">Low</TableHead>
                      <TableHead className="text-right">Close</TableHead>
                      <TableHead className="text-right">Volume</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {prices.slice(-30).reverse().map((p) => (
                      <TableRow key={p.date} data-testid={`us-price-row-${p.date}`}>
                        <TableCell className="tabular-nums">{fmtDate(p.date)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtUsd(p.open)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtUsd(p.high)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtUsd(p.low)}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{fmtUsd(p.close)}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{Number(p.volume || 0).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Peers */}
        <TabsContent value="peers" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Top {peers.length || 0} {company.sector || "sector"} peers</CardTitle>
              <CardDescription className="text-xs">Same GICS sector, ranked by composite score.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {peers.length === 0 ? (
                <div className="p-8 text-sm text-center text-muted-foreground">No peers found in {company.sector || "this sector"}.</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Symbol</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead className="text-right">Price</TableHead>
                        <TableHead className="text-right">Score</TableHead>
                        <TableHead>Signal</TableHead>
                        <TableHead>Valuation</TableHead>
                        <TableHead className="text-right">Target</TableHead>
                        <TableHead className="text-right">Upside</TableHead>
                        <TableHead>Type</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {peers.map((p) => {
                        const cls =
                          p.upside_pct > 0 ? "text-emerald-600 dark:text-emerald-400" :
                          p.upside_pct < 0 ? "text-destructive" : "text-muted-foreground";
                        return (
                          <TableRow
                            key={p.symbol}
                            className="cursor-pointer hover:bg-muted/40"
                            onClick={() => navigate(`/us-stocks/${encodeURIComponent(p.symbol)}`)}
                            data-testid={`us-peer-row-${p.symbol}`}
                          >
                            <TableCell>
                              <Link
                                className="font-mono font-semibold text-primary hover:underline"
                                to={`/us-stocks/${encodeURIComponent(p.symbol)}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {p.symbol}
                              </Link>
                            </TableCell>
                            <TableCell className="text-sm">{p.company_name || "—"}</TableCell>
                            <TableCell className="text-right tabular-nums">{fmtUsd(p.latest_close)}</TableCell>
                            <TableCell className="text-right tabular-nums font-semibold">{p.composite_score?.toFixed(1) ?? "—"}</TableCell>
                            <TableCell><SignalBadge signal={p.signal} /></TableCell>
                            <TableCell><ValuationBadge status={p.valuation_status} /></TableCell>
                            <TableCell className="text-right tabular-nums">{fmtUsd(p.base_target_price)}</TableCell>
                            <TableCell className={`text-right tabular-nums ${cls}`}>{p.upside_pct == null ? "—" : fmtPct(p.upside_pct)}</TableCell>
                            <TableCell><CompanyTypeBadge type={p.company_type} /></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Valuation Methods */}
        <TabsContent value="valuation" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                Valuation Methods
                <CompanyTypeBadge type={score.company_type} reason={score.company_type_reason} />
              </CardTitle>
              <CardDescription className="text-xs max-w-3xl">
                Different company types require different valuation math. Methods marked <span className="text-base align-middle">⚫</span>
                <strong> N/A</strong> are <em>not applicable</em> to this company type — hover for the methodology reason.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ValuationMethodsTable score={score} />

              <div className="mt-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground">Latest close</div>
                  <div className="text-xl font-semibold tabular-nums">{fmtUsd(score.latest_close)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Base case target</div>
                  <div className="text-xl font-semibold tabular-nums">{fmtUsd(score.base_target_price)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Implied upside</div>
                  <div className={`text-xl font-semibold tabular-nums ${upClass}`}>
                    {score.upside_pct == null ? "—" : fmtPct(score.upside_pct)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Verdict</div>
                  <div><ValuationBadge status={score.valuation_status} /></div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Deep AI */}
        <TabsContent value="deep-ai" className="space-y-4">
          <UsDeepAnalysisSection symbol={symbol} />
        </TabsContent>
      </Tabs>

      <Separator />
      <div className="text-xs text-muted-foreground">
        Data via <span className="font-mono">yfinance</span>. Composite scores recompute Mon–Fri after US market close.
        This is research output, not investment advice.
      </div>
    </div>
  );
};

export default UsStockDetailPage;
